import { useCallback, useEffect, useState } from "react";
import { ArrowsClockwise, Plus, Trash, WhatsappLogo, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const STATUS_BADGE = {
  APPROVED: ["bg-emerald-100 text-emerald-800 border-emerald-300", "APPROVATO"],
  PENDING: ["bg-amber-100 text-amber-800 border-amber-300", "IN REVISIONE"],
  REJECTED: ["bg-rose-100 text-rose-700 border-rose-300", "RIFIUTATO"],
  PAUSED: ["bg-zinc-100 text-zinc-500 border-zinc-300", "IN PAUSA"],
  DISABLED: ["bg-zinc-100 text-zinc-500 border-zinc-300", "DISABILITATO"],
  BOZZA: ["bg-sky-100 text-sky-800 border-sky-300", "BOZZA LOCALE"],
};
const CAT_BADGE = { UTILITY: "bg-sky-50 text-sky-700", MARKETING: "bg-violet-50 text-violet-700", AUTHENTICATION: "bg-zinc-100 text-zinc-600" };
const EMPTY = { name: "", category: "UTILITY", language: "it", body: "", header_text: "", footer: "", buttons: [], examples: [] };

export default function WaTemplates() {
  const [list, setList] = useState(null);
  const [options, setOptions] = useState({ categories: [], languages: [] });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/integrations/whatsapp/templates").then((r) => setList(r.data)).catch((e) => { setList([]); toast.error(apiError(e)); });
  }, []);

  useEffect(() => {
    load();
    api.get("/integrations/whatsapp/templates/meta-options").then((r) => setOptions(r.data)).catch(() => {});
  }, [load]);

  const nVars = (form.body.match(/\{\{\d+\}\}/g) || []).length;

  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/whatsapp/templates/sync");
      toast.success(`Sincronizzati ${r.data.synced} template da Meta`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/integrations/whatsapp/templates", {
        ...form,
        header_text: form.header_text || null,
        footer: form.footer || null,
        examples: form.examples.slice(0, nVars),
      });
      toast.success(r.data.status === "PENDING"
        ? "Template inviato a Meta: in revisione"
        : `Template creato (${r.data.status})`);
      setShowForm(false);
      setForm(EMPTY);
      load();
    } catch (e2) { toast.error(apiError(e2)); } finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Eliminare il template "${t.name}" anche da Meta?`)) return;
    try {
      await api.delete(`/integrations/whatsapp/templates/${t.id}`);
      toast.success("Template eliminato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addButton = (type) => setForm({ ...form, buttons: [...form.buttons, { type, text: "", url: "" }] });

  const bodyOf = (t) => (t.components || []).find((c) => c.type === "BODY")?.text || t.body || "";
  const headerOf = (t) => (t.components || []).find((c) => c.type === "HEADER")?.text || "";
  const footerOf = (t) => (t.components || []).find((c) => c.type === "FOOTER")?.text || "";
  const buttonsOf = (t) => (t.components || []).find((c) => c.type === "BUTTONS")?.buttons || [];

  const previewText = (txt) => txt.replace(/\{\{(\d+)\}\}/g, (_, i) => form.examples[parseInt(i) - 1] || `{{${i}}}`);

  return (
    <section className="space-y-4" data-testid="wa-tpl-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg font-bold text-zinc-900 flex items-center gap-2">
          <WhatsappLogo size={20} className="text-emerald-600" weight="fill" /> Template messaggi
        </h2>
        <div className="flex gap-2">
          <button onClick={sync} disabled={busy} data-testid="wa-tpl-sync"
            className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-bold text-zinc-700 flex items-center gap-1.5 disabled:opacity-40">
            <ArrowsClockwise size={14} weight="bold" /> Sincronizza da Meta
          </button>
          <button onClick={() => setShowForm(true)} data-testid="wa-tpl-new"
            className="h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
            <Plus size={14} weight="bold" /> Nuovo template
          </button>
        </div>
      </div>
      <p className="text-xs text-zinc-500">
        I template vivono sul WhatsApp Business Account del tuo ristorante e servono per i messaggi automatici
        fuori dalla finestra 24h. Meta li revisiona: solo quelli <b>approvati</b> sono usabili nei workflow.
      </p>

      {list === null ? (
        <div className="text-sm text-zinc-400 py-6 text-center">Caricamento…</div>
      ) : list.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-300 rounded-lg p-8 text-center text-sm text-zinc-400" data-testid="wa-tpl-empty">
          Nessun template. Crea il primo e invialo in revisione a Meta.
        </div>
      ) : (
        <div className="space-y-2.5">
          {list.map((t) => {
            const [cls, label] = STATUS_BADGE[t.status] || STATUS_BADGE.BOZZA;
            return (
              <div key={t.id} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`wa-tpl-row-${t.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-bold text-zinc-900">{t.name}</span>
                  <span className="text-[10px] text-zinc-400 font-semibold">{t.language}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${CAT_BADGE[t.category] || CAT_BADGE.UTILITY}`}>{t.category}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${cls}`} data-testid={`wa-tpl-status-${t.id}`}>{label}</span>
                  <div className="flex-1" />
                  <button onClick={() => del(t)} data-testid={`wa-tpl-del-${t.id}`}
                    className="p-1.5 text-zinc-400 hover:text-rose-600"><Trash size={15} /></button>
                </div>
                <p className="text-xs text-zinc-500 mt-1.5 whitespace-pre-wrap">{bodyOf(t)}</p>
                {t.status === "REJECTED" && t.rejected_reason && (
                  <p className="text-[11px] text-rose-600 mt-1.5" data-testid={`wa-tpl-reason-${t.id}`}>
                    Motivo del rifiuto: {t.rejected_reason}
                  </p>
                )}
                {t.status === "PENDING" && (
                  <p className="text-[11px] text-amber-700 mt-1.5">In revisione da Meta — aggiorna con "Sincronizza da Meta".</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} data-testid="wa-tpl-form" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-2xl rounded-t-xl sm:rounded-lg p-5 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg">Nuovo template WhatsApp</h3>
              <button type="button" onClick={() => setShowForm(false)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-zinc-600 block mb-1">Nome (minuscole, numeri, _)</label>
                  <input required value={form.name} pattern="[a-z0-9_]+" placeholder="conferma_prenotazione"
                    onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })}
                    className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm font-mono" data-testid="wa-tpl-name" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-semibold text-zinc-600 block mb-1">Categoria</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm" data-testid="wa-tpl-category">
                      {options.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-zinc-600 block mb-1">Lingua</label>
                    <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm" data-testid="wa-tpl-language">
                      {options.languages.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-600 block mb-1">Header (opzionale, max 60)</label>
                  <input value={form.header_text} maxLength={60} placeholder="Prenotazione confermata"
                    onChange={(e) => setForm({ ...form, header_text: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm" data-testid="wa-tpl-header" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-zinc-600 block mb-1">Corpo — variabili come {"{{1}}"}, {"{{2}}"}…</label>
                  <textarea required rows={4} maxLength={1024} value={form.body}
                    placeholder={"Ciao {{1}}, la tua prenotazione per il {{2}} è confermata."}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm" data-testid="wa-tpl-body" />
                </div>
                {nVars > 0 && (
                  <div className="space-y-1.5" data-testid="wa-tpl-examples">
                    <label className="text-xs font-semibold text-zinc-600 block">Esempi per Meta (obbligatori per la revisione)</label>
                    {Array.from({ length: nVars }).map((_, i) => (
                      <input key={i} required value={form.examples[i] || ""} placeholder={`Esempio per {{${i + 1}}} (es. Mario)`}
                        onChange={(e) => {
                          const ex = [...form.examples];
                          ex[i] = e.target.value;
                          setForm({ ...form, examples: ex });
                        }}
                        className="w-full h-9 px-3 rounded-md border border-zinc-300 text-xs" data-testid={`wa-tpl-example-${i}`} />
                    ))}
                  </div>
                )}
                <div>
                  <label className="text-xs font-semibold text-zinc-600 block mb-1">Footer (opzionale, max 60)</label>
                  <input value={form.footer} maxLength={60} placeholder="Rispondi per assistenza"
                    onChange={(e) => setForm({ ...form, footer: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm" data-testid="wa-tpl-footer" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-zinc-600">Pulsanti (max 3)</label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => addButton("QUICK_REPLY")} data-testid="wa-tpl-btn-add-qr"
                        className="text-[11px] font-bold text-sky-700">+ Risposta rapida</button>
                      <button type="button" onClick={() => addButton("URL")} data-testid="wa-tpl-btn-add-url"
                        className="text-[11px] font-bold text-sky-700">+ Link</button>
                    </div>
                  </div>
                  {form.buttons.map((b, i) => (
                    <div key={i} className="flex gap-1.5 mb-1.5">
                      <input value={b.text} placeholder={b.type === "URL" ? "Testo pulsante" : "Risposta"}
                        onChange={(e) => setForm({ ...form, buttons: form.buttons.map((x, j) => j === i ? { ...x, text: e.target.value } : x) })}
                        className="flex-1 h-9 px-2.5 rounded-md border border-zinc-300 text-xs" data-testid={`wa-tpl-btn-text-${i}`} />
                      {b.type === "URL" && (
                        <input value={b.url} placeholder="https://…"
                          onChange={(e) => setForm({ ...form, buttons: form.buttons.map((x, j) => j === i ? { ...x, url: e.target.value } : x) })}
                          className="flex-1 h-9 px-2.5 rounded-md border border-zinc-300 text-xs" data-testid={`wa-tpl-btn-url-${i}`} />
                      )}
                      <button type="button" onClick={() => setForm({ ...form, buttons: form.buttons.filter((_, j) => j !== i) })}
                        data-testid={`wa-tpl-btn-del-${i}`} className="p-1.5 text-zinc-400 hover:text-rose-600"><Trash size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-zinc-600 block mb-1.5">Anteprima</label>
                <div className="rounded-xl p-4 min-h-[220px]" style={{ background: "#E5DDD5" }} data-testid="wa-tpl-preview">
                  <div className="bg-white rounded-lg shadow-sm max-w-[85%] overflow-hidden">
                    {form.header_text && (
                      <div className="px-3 pt-2.5 text-sm font-bold text-zinc-900">{previewText(form.header_text)}</div>
                    )}
                    <div className="px-3 py-2 text-sm text-zinc-800 whitespace-pre-wrap">
                      {form.body ? previewText(form.body) : <span className="text-zinc-300">Il corpo del messaggio…</span>}
                    </div>
                    {form.footer && <div className="px-3 pb-1.5 text-[11px] text-zinc-400">{form.footer}</div>}
                    <div className="px-3 pb-1.5 text-right text-[10px] text-zinc-400">12:00 ✓✓</div>
                    {form.buttons.map((b, i) => (
                      <div key={i} className="border-t border-zinc-100 text-center py-2 text-[13px] font-medium text-sky-600">
                        {b.type === "URL" ? "↗ " : "↩ "}{b.text || "Pulsante"}
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-zinc-400 mt-2">
                  All'invio, Meta revisiona il template (di solito pochi minuti). Stato visibile qui dopo la sincronizzazione.
                </p>
              </div>
            </div>
            <button disabled={busy} className="mt-4 w-full h-12 rounded-md bg-emerald-600 text-white text-sm font-bold disabled:opacity-40"
              data-testid="wa-tpl-submit">
              {busy ? "Invio a Meta…" : "Invia in revisione a Meta"}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
